export enum EquationTemplateMenuEnum {
  symbols = 'symbols',
  math = 'math',
  physical = 'physical',
  chemistry = 'chemistry',
}

export enum EquationSymbolsEnum {
  math = 'math',
  func = 'func',
  arrow = 'arrow',
  greek = 'greek',
}

// Helper function to get translated template menu data
export const getEquationTemplateMenu = (t: (key: string) => string) => [
  {
    icon: 'IcMenuSymbol',
    title: t('editor.latex.common_symbols'),
    isGroup: true,
    type: EquationTemplateMenuEnum.symbols,
    group: [
      {
        title: t('editor.latex.math'),
        value: EquationSymbolsEnum.math,
      },
      {
        title: t('editor.latex.function'),
        value: EquationSymbolsEnum.func,
      },
      {
        title: t('editor.latex.arrow'),
        value: EquationSymbolsEnum.arrow,
      },
      {
        title: t('editor.latex.greek_letters'),
        value: EquationSymbolsEnum.greek,
      },
    ],
  },
  {
    icon: 'IcMenuMath',
    title: t('common.math_formula'),
    isGroup: false,
    type: EquationTemplateMenuEnum.math,
    group: [],
  },
  {
    icon: 'IcMenuPhysics',
    title: t('editor.latex.physics_formula'),
    isGroup: false,
    type: EquationTemplateMenuEnum.physical,
    group: [],
  },
  {
    icon: 'IcMenuChemistry',
    title: t('editor.latex.chemistry_formula'),
    isGroup: false,
    type: EquationTemplateMenuEnum.chemistry,
    group: [],
  },
];

// Keep the old export for backward compatibility - it will be static but deprecated
export const equationTemplateMenu = [
  {
    icon: 'IcMenuSymbol',
    titleKey: 'editor.latex.common_symbols',
    isGroup: true,
    type: EquationTemplateMenuEnum.symbols,
    group: [
      {
        titleKey: 'editor.latex.math',
        value: EquationSymbolsEnum.math,
      },
      {
        titleKey: 'editor.latex.function',
        value: EquationSymbolsEnum.func,
      },
      {
        titleKey: 'editor.latex.arrow',
        value: EquationSymbolsEnum.arrow,
      },
      {
        titleKey: 'editor.latex.greek_letters',
        value: EquationSymbolsEnum.greek,
      },
    ],
  },
  {
    icon: 'IcMenuMath',
    titleKey: 'editor.latex.math_formula',
    isGroup: false,
    type: EquationTemplateMenuEnum.math,
    group: [],
  },
  {
    icon: 'IcMenuPhysics',
    titleKey: 'editor.latex.physics_formula',
    isGroup: false,
    type: EquationTemplateMenuEnum.physical,
    group: [],
  },
  {
    icon: 'IcMenuChemistry',
    titleKey: 'editor.latex.chemistry_formula',
    isGroup: false,
    type: EquationTemplateMenuEnum.chemistry,
    group: [],
  },
];

// Helper function to get translated formula data
export const getEquationFormula = (t: (key: string) => string) => ({
  symbols: {
    [EquationSymbolsEnum.math]: [
      {
        symbols: [
          {
            cmd: '\\times',
          },
          {
            cmd: '\\div',
          },
          {
            cmd: '\\pm',
          },
          {
            cmd: '\\mp',
          },
          {
            cmd: '\\cdot',
          },
          {
            cmd: '\\star',
          },
          {
            cmd: '\\%',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\sqrt{ab}',
          },
          {
            cmd: '\\sqrt[n]{ab}',
          },
          {
            cmd: '\\log_{a}{b}',
          },
          {
            cmd: '\\lg{ab}',
          },
          {
            cmd: 'a^{b}',
          },
          {
            cmd: 'a_{b}',
          },
          {
            cmd: 'c_a^b',
          },
          {
            cmd: '\\widetilde{ab}',
          },
          {
            cmd: '\\widehat{ab}',
          },
          {
            cmd: '\\overleftarrow{ab}',
          },
          {
            cmd: '\\overrightarrow{ab}',
          },
          {
            cmd: '\\overbrace{ab}',
          },
          {
            cmd: '\\underbrace{ab}',
          },
          {
            cmd: '\\underline{ab}',
          },
          {
            cmd: '\\overline{ab}',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\neq',
          },
          {
            cmd: '\\leq',
          },
          {
            cmd: '\\geq',
          },
          {
            cmd: '\\sim',
          },
          {
            cmd: '\\approx',
          },
          {
            cmd: '\\cong',
          },
          {
            cmd: '\\equiv',
          },
          {
            cmd: '\\propto',
          },
          {
            cmd: '\\ll',
          },
          {
            cmd: '\\gg',
          },
          {
            cmd: '\\in',
          },
          {
            cmd: '\\subset',
          },
          {
            cmd: '\\subseteq',
          },
          {
            cmd: '\\prec',
          },
          {
            cmd: '\\preceq',
          },
          {
            cmd: '\\simeq',
          },
          {
            cmd: '\\asymp',
          },
          {
            cmd: '\\doteq',
          },
          {
            cmd: '\\succ',
          },
          {
            cmd: '\\succeq',
          },
          {
            cmd: '\\sqsubseteq',
          },
          {
            cmd: '\\sqsupseteq',
          },
          {
            cmd: '\\ni',
          },
          {
            cmd: '\\models',
          },
          {
            cmd: '\\vdash',
          },
          {
            cmd: '\\dashv',
          },
          {
            cmd: '\\perp',
          },
          {
            cmd: '\\mid',
          },
          {
            cmd: '\\parallel',
          },
          {
            cmd: '\\smile',
          },
          {
            cmd: '\\frown',
          },
          {
            cmd: '\\bowtie',
          },
          {
            cmd: '\\unlhd',
          },
          {
            cmd: '\\unrhd',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\hat{a}',
          },
          {
            cmd: '\\check{a}',
          },
          {
            cmd: '\\breve{a}',
          },
          {
            cmd: '\\acute{a}',
          },
          {
            cmd: '\\grave{a}',
          },
          {
            cmd: '\\tilde{a}',
          },
          {
            cmd: '\\bar{a}',
          },
          {
            cmd: '\\vec{a}',
          },
          {
            cmd: '\\dot{a}',
          },
          {
            cmd: '\\ddot{a}',
          },
        ],
      },
    ],
    [EquationSymbolsEnum.func]: [
      {
        symbols: [
          {
            cmd: '\\frac{ab}{cd}',
          },
          {
            cmd: '\\frac{\\partial a}{\\partial b}',
          },
          {
            cmd: '\\frac{\\text{d}x}{\\text{d}y}',
          },
          {
            cmd: '\\lim\\limits_{a \\rightarrow b}',
          },
          {
            cmd: '\\int_{a}^{b}',
          },
          {
            cmd: '\\oint_a^b',
          },
          {
            cmd: '\\prod\\limits_a^b',
          },
          {
            cmd: '\\coprod\\limits_a^b',
          },
          {
            cmd: '\\bigcap\\limits_a^b',
          },
          {
            cmd: '\\bigcup\\limits_a^b',
          },
          {
            cmd: '\\bigvee\\limits_a^b',
          },
          {
            cmd: '\\bigwedge\\limits_a^b',
          },
          {
            cmd: '\\bigsqcup\\limits_a^b',
          },
          {
            cmd: '\\sum\\limits_a^b',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\left(\\begin{array}{c}a\\\\ b\\end{array}\\right)',
          },
          {
            cmd: '\\begin{bmatrix}a & b \\\\c & d \\end{bmatrix}',
          },
          {
            cmd: '\\begin{cases}a & x = 0\\\\b & x > 0\\end{cases}',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\arccos',
          },
          {
            cmd: '\\arcsin',
          },
          {
            cmd: '\\arctan',
          },
          {
            cmd: '\\cos',
          },
          {
            cmd: '\\cosh',
          },
          {
            cmd: '\\cot',
          },
          {
            cmd: '\\coth',
          },
          {
            cmd: '\\csc',
          },
          {
            cmd: '\\sec',
          },
          {
            cmd: '\\sin',
          },
          {
            cmd: '\\sinh',
          },
          {
            cmd: '\\tan',
          },
          {
            cmd: '\\tanh',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\exp',
          },
          {
            cmd: '\\log',
          },
          {
            cmd: '\\ln',
          },
          {
            cmd: '\\max',
          },
          {
            cmd: '\\min',
          },
          {
            cmd: '\\sup',
          },
          {
            cmd: '\\inf',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\lim',
          },
          {
            cmd: '\\gcd',
          },
          {
            cmd: '\\hom',
          },
          {
            cmd: '\\ker',
          },
          {
            cmd: '\\det',
          },
          {
            cmd: '\\bmod',
          },
        ],
      },
    ],
    [EquationSymbolsEnum.arrow]: [
      {
        symbols: [
          {
            cmd: '()',
          },
          {
            cmd: '[]',
          },
          {
            cmd: '\\{\\}',
          },
          {
            cmd: '\\lfloor',
          },
          {
            cmd: '\\lceil',
          },
          {
            cmd: '\\rfloor',
          },
          {
            cmd: '\\rceil',
          },
          {
            cmd: '\\langle',
          },
          {
            cmd: '\\rangle',
          },
          {
            cmd: '\\backslash',
          },
          {
            cmd: '\\cap',
          },
          {
            cmd: '\\cup',
          },
          {
            cmd: '\\uplus',
          },
          {
            cmd: '\\sqcap',
          },
          {
            cmd: '\\sqcup',
          },
          {
            cmd: '\\vee',
          },
          {
            cmd: '\\wedge',
          },
          {
            cmd: '\\wr',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\leftarrow',
          },
          {
            cmd: '\\Leftarrow',
          },
          {
            cmd: '\\rightarrow',
          },
          {
            cmd: '\\Rightarrow',
          },
          {
            cmd: '\\leftrightarrow',
          },
          {
            cmd: '\\Leftrightarrow',
          },
          {
            cmd: '\\mapsto',
          },
          {
            cmd: '\\leftharpoonup',
          },
          {
            cmd: '\\leftharpoondown',
          },
          {
            cmd: '\\uparrow',
          },
          {
            cmd: '\\downarrow',
          },
          {
            cmd: '\\leftleftarrows',
          },
          {
            cmd: '\\rightrightarrows',
          },
          {
            cmd: '\\leftrightarrows',
          },
          {
            cmd: '\\rightleftarrows',
          },
        ],
      },
    ],
    [EquationSymbolsEnum.greek]: [
      {
        symbols: [
          {
            cmd: '\\alpha',
          },
          {
            cmd: '\\beta',
          },
          {
            cmd: '\\gamma',
          },
          {
            cmd: '\\delta',
          },
          {
            cmd: '\\epsilon',
          },
          {
            cmd: '\\zeta',
          },
          {
            cmd: '\\eta',
          },
          {
            cmd: '\\theta',
          },
          {
            cmd: '\\iota',
          },
          {
            cmd: '\\kappa',
          },
          {
            cmd: '\\lambda',
          },
          {
            cmd: '\\mu',
          },
          {
            cmd: '\\nu',
          },
          {
            cmd: '\\xi',
          },
          {
            cmd: '\\pi',
          },
          {
            cmd: '\\rho',
          },
          {
            cmd: '\\sigma',
          },
          {
            cmd: '\\tau',
          },
          {
            cmd: '\\upsilon',
          },
          {
            cmd: '\\phi',
          },
          {
            cmd: '\\chi',
          },
          {
            cmd: '\\psi',
          },
          {
            cmd: '\\omega',
          },
        ],
      },
    ],
  },
  disciplineFormula: {
    [EquationTemplateMenuEnum.math]: [
      {
        title: t('editor.latex.fourier_series'),
        symbols: [
          {
            cmd: 'f(x) = {{{a_0}} \\over 2} + \\sum\\limits_{n = 1}^\\infty {({a_n}\\cos {nx} + {b_n}\\sin {nx})}',
          },
        ],
      },
      {
        title: t('editor.latex.taylor_expansion'),
        symbols: [
          {
            cmd: 'e ^ { x } = 1 + \\frac { x } { 1 ! } + \\frac { x ^ { 2 } } { 2 ! } + \\frac { x ^ { 3 } } { 3 ! } + \\cdots , \\quad - \\infty < x < \\infty',
          },
        ],
      },
      {
        title: t('editor.latex.gauss_formula'),
        symbols: [
          {
            cmd: '\\iiint _ { \\Omega } \\left( \\frac { \\partial {P} } { \\partial {x} } + \\frac { \\partial {Q} } { \\partial {y} } + \\frac { \\partial {R} }{ \\partial {z} } \\right) \\mathrm { d } V = \\oint _ { \\partial \\Omega } ( P \\cos \\alpha + Q \\cos \\beta + R \\cos \\gamma ) \\mathrm{ d} S',
          },
        ],
      },
      {
        title: t('editor.latex.definite_integral'),
        symbols: [
          {
            cmd: '\\lim\\limits_ { n \\rightarrow + \\infty } \\sum _ { i = 1 } ^ { n } f \\left[ a + \\frac { i } { n } ( b - a ) \\right] \\frac { b - a } { n } = \\int _ { a } ^ { b } f ( x ) dx',
          },
        ],
      },
      {
        title: t('editor.latex.expansion_of_sum'),
        symbols: [
          {
            cmd: '( 1 + x ) ^ { n } = 1 + \\frac { n x } { 1 ! } + \\frac { n ( n - 1 ) x ^ { 2 } } { 2 ! } + \\cdots',
          },
        ],
      },
      {
        title: t('editor.latex.trigonometric_identity_1'),
        symbols: [
          {
            cmd: '\\sin \\alpha \\pm \\sin \\beta = 2 \\sin \\frac { 1 } { 2 } ( \\alpha \\pm \\beta ) \\cos \\frac { 1 } { 2 } ( \\alpha \\mp \\beta )',
          },
        ],
      },
      {
        title: t('editor.latex.trigonometric_identity_2'),
        symbols: [
          {
            cmd: '\\cos \\alpha + \\cos \\beta = 2 \\cos \\frac { 1 } { 2 } ( \\alpha + \\beta ) \\cos \\frac { 1 } { 2 } ( \\alpha - \\beta )',
          },
        ],
      },
      {
        title: t('editor.latex.euler_formula'),
        symbols: [
          {
            cmd: '{e^{ix}} = \\cos {x} + i\\sin {x}',
          },
        ],
      },
      {
        title: t('editor.latex.green_formula'),
        symbols: [
          {
            cmd: '\\iint\\limits_D {({{\\partial Q} \\over {\\partial x}} - {{\\partial P} \\over {\\partial y}})dxdy = \\oint\\limits_L {Pdx + Qdy} }',
          },
        ],
      },
      {
        title: t('editor.latex.bernoulli_equation'),
        symbols: [
          {
            cmd: '{{dy} \\over {dx}} + P(x)y = Q(x){y^n}(n \\ne 0,1)',
          },
        ],
      },
      {
        title: t('editor.latex.total_differential_equation'),
        symbols: [
          {
            cmd: 'du(x,y) = P(x,y)dx + Q(x,y)dy = 0',
          },
        ],
      },
      {
        title: t('editor.latex.inhomogeneous_equation'),
        symbols: [
          {
            cmd: 'y = (\\int {Q(x){e^{\\int {P(x)dx} }}dx + C){e^{ - \\int {P(x)dx} }}}',
          },
        ],
      },
      {
        title: t('editor.latex.cauchy_mean_value_theorem'),
        symbols: [
          {
            cmd: "\\frac{{f(b) - f(a)}}{{F(b) - F(a)}} = \\frac{{f'(\\xi )}}{{F'(\\xi )}}",
          },
        ],
      },
      {
        title: t('editor.latex.lagrange_mean_value_theorem'),
        symbols: [
          {
            cmd: "f(b) - f(a) = f'(\\xi )(b - a)",
          },
        ],
      },
      {
        title: t('editor.latex.surface_area'),
        symbols: [
          {
            cmd: 'A = \\iint\\limits_D {\\sqrt {1 + {{\\left( {{{\\partial z} \\over {\\partial x}}} \\right)}^2} + {{\\left( {{{\\partial z} \\over {\\partial y}}} \\right)}^2}} dxdy}',
          },
        ],
      },
      {
        title: t('editor.latex.multiple_integral'),
        symbols: [
          {
            cmd: "\\iint\\limits_D {f(x,y)dxdy} = \\iint\\limits_{D'} {f(r\\cos \\theta ,r\\sin \\theta )rdrd\\theta}",
          },
        ],
      },
      {
        title: t('editor.latex.derivative_formula'),
        symbols: [
          {
            cmd: "(\\arcsin x)' = \\frac{1}{{\\sqrt {1 - {x^2}} }}",
          },
        ],
      },
      {
        title: t('editor.latex.trigonometric_function_integral'),
        symbols: [
          {
            cmd: '\\int {tgxdx = - \\ln \\left| {\\cos x} \\right| + C}',
          },
        ],
      },
      {
        title: t('editor.latex.quadric_surface'),
        symbols: [
          {
            cmd: '\\frac{{{x^2}}}{{{a^2}}} + \\frac{{{y^2}}}{{{b^2}}} - \\frac{{{z^2}}}{{{c^2}}} = 1',
          },
        ],
      },
      {
        title: t('editor.latex.second_order_derivative'),
        symbols: [
          {
            cmd: '{{{d^2}y} \\over {d{x^2}}} + P(x){{dy} \\over {dx}} + Q(x)y = f(x)',
          },
        ],
      },
      {
        title: t('editor.latex.directional_derivative'),
        symbols: [
          {
            cmd: '\\frac{{\\partial f}}{{\\partial l}} = \\frac{{\\partial f}}{{\\partial x}}\\cos \\phi + \\frac{{\\partial f}}{{\\partial y}}\\sin \\phi',
          },
        ],
      },
    ],
    [EquationTemplateMenuEnum.physical]: [
      {
        title: t('editor.latex.newton_first_law'),
        symbols: [
          {
            cmd: '\\sum \\vec{F}_{i}=\\frac{d \\vec{v}}{d t}=0',
          },
        ],
      },
      {
        title: t('editor.latex.newton_second_law'),
        symbols: [
          {
            cmd: '\\vec{F}=m \\vec{a}=m \\frac{d^{2} \\vec{r}}{d t^{2}}',
          },
        ],
      },
      {
        title: t('editor.latex.newton_third_law'),
        symbols: [
          {
            cmd: '\\vec{F}_{12}=-\\vec{F}_{21}',
          },
        ],
      },
      {
        title: t('editor.latex.gravitational_potential_energy'),
        symbols: [
          {
            cmd: '{E_p} = -\\frac{{GMm}}{r}',
          },
        ],
      },
      {
        title: t('editor.latex.coulomb_law'),
        symbols: [
          {
            cmd: '\\vec{F}=k \\frac{Q q}{r^{2}} \\hat{r}',
          },
        ],
      },
      {
        title: t('editor.latex.electrostatic_field_loop_theorem'),
        symbols: [
          {
            cmd: '\\oint_{L} \\vec{E} \\cdot d \\vec{I}=0',
          },
        ],
      },
      {
        title: t('editor.latex.biot_savart_law'),
        symbols: [
          {
            cmd: 'd \\vec{B}=\\frac{\\mu_{0}}{4 \\pi} \\frac{I d I \\times \\vec{r}}{r^{3}}=\\frac{\\mu_{0}}{4 \\pi} \\frac{I d I \\sin \\theta}{r^{2}}',
          },
        ],
      },
      {
        title: t('editor.latex.ampere_law'),
        symbols: [
          {
            cmd: 'd \\vec{F}= Id \\vec{l} \\times \\vec{B}',
          },
        ],
      },
      {
        title: t('editor.latex.faraday_electromagnetic_induction_law'),
        symbols: [
          {
            cmd: 'E = n{{ \\Delta \\Phi } \\over {\\Delta {t} }}',
          },
        ],
      },
      {
        title: t('editor.latex.gauss_law'),
        symbols: [
          {
            cmd: '\\left.\\Phi_{e}=\\oint \\vec{E} \\cdot d \\vec{S}=\\frac{1}{\\varepsilon_{0}}\\right\\rangle q',
          },
        ],
      },
      {
        title: t('editor.latex.electric_field_loop_theorem'),
        symbols: [
          {
            cmd: '\\oint \\vec{E} \\cdot d \\vec{I}=-\\frac{d \\varphi_{B}}{d t}',
          },
        ],
      },
      {
        title: t('editor.latex.ampere_loop_theorem'),
        symbols: [
          {
            cmd: '\\oint \\vec{B} \\cdot d \\vec{I}=\\mu_{0} I+\\mu_{0} I_{d}',
          },
        ],
      },
      {
        title: t('editor.latex.joule_law'),
        symbols: [
          {
            cmd: 'Q = I ^ { 2 } R \\mathrm { t }',
          },
        ],
      },
      {
        title: t('editor.latex.law_of_universal_gravitation'),
        symbols: [
          {
            cmd: 'F = G{{Mm} \\over {{r^2}}}',
          },
        ],
      },
      {
        title: t('editor.latex.einstein_photoelectric_equation'),
        symbols: [
          {
            cmd: '{E_k} = hv - {W_0}',
          },
        ],
      },
      {
        title: t('editor.latex.de_broglie_wavelength'),
        symbols: [
          {
            cmd: '\\lambda = \\frac{{ \\frac{{{c^2}}}{v}}}{{ \\frac{{m{c^2}}}{h}}} = \\frac{h}{{mv}} = \\frac{h}{p}',
          },
        ],
      },
      {
        title: t('editor.latex.heisenberg_uncertainty_principle'),
        symbols: [
          {
            cmd: '\\Delta {x} \\Delta {p} \\ge \\frac{h}{{4 \\pi }}',
          },
        ],
      },
      {
        title: t('editor.latex.special_relativity_length'),
        symbols: [
          {
            cmd: 'l = {l_0} \\sqrt {1 - {{( \\frac{v}{c})}^2}}',
          },
        ],
      },
      {
        title: t('editor.latex.simple_harmonic_motion'),
        symbols: [
          {
            cmd: '{y_0} = A \\cos ( \\omega {t} + { \\varphi _0})',
          },
        ],
      },
      {
        title: t('editor.latex.wave_equation'),
        symbols: [
          {
            cmd: 'y(t) = A \\cos ( \\frac{{2 \\pi {x}}}{ \\lambda } + \\varphi )',
          },
        ],
      },
      {
        title: t('editor.latex.doppler_effect'),
        symbols: [
          {
            cmd: "f' = \\left( { \\frac{{v \\pm {v_0}}}{{v \\pm {v_s}}}} \\right)f",
          },
        ],
      },
    ],
    [EquationTemplateMenuEnum.chemistry]: [
      {
        title: t('editor.latex.aluminum_dilute_sulfuric_acid'),
        symbols: [
          {
            cmd: '\\ce{2Al + 3{H_2}S{O_4} = A{l_2}{(S{O_4})_3} + 3{H_2} \\uparrow}',
          },
        ],
      },
      {
        title: t('editor.latex.aluminum_dilute_hydrochloric_acid'),
        symbols: [
          {
            cmd: '\\ce{2Al + 6HCl = 2AlC{l_3} + 3{H_2} \\uparrow}',
          },
        ],
      },
      {
        title: t('editor.latex.magnesium_dilute_sulfuric_acid'),
        symbols: [
          {
            cmd: '\\ce{Mg + {H_2}S{O_4} = MgS{O_4} + {H_2} \\uparrow}',
          },
        ],
      },
      {
        title: t('editor.latex.magnesium_dilute_hydrochloric_acid'),
        symbols: [
          {
            cmd: '\\ce{Mg + 2HCl = MgC{l_2} + {H_2} \\uparrow}',
          },
        ],
      },
      {
        title: t('editor.latex.iron_copper_sulfate_solution'),
        symbols: [
          {
            cmd: '\\ce{Fe + CuS{O_4} = FeS{O_4} + Cu}',
          },
        ],
      },
      {
        title: t('editor.latex.iron_dilute_sulfuric_acid'),
        symbols: [
          {
            cmd: '\\ce{Fe + {H_2}S{O_4} = FeS{O_4} + {H_2} \\uparrow}',
          },
        ],
      },
      {
        title: t('editor.latex.iron_dilute_hydrochloric_acid'),
        symbols: [
          {
            cmd: '\\ce{Fe + 2HCl = FeC{l_2} + {H_2} \\uparrow}',
          },
        ],
      },
      {
        title: t('editor.latex.zinc_dilute_sulfuric_acid'),
        symbols: [
          {
            cmd: '\\ce{Zn + {H_2}S{O_4} = ZnS{O_4} + {H_2} \\uparrow}',
          },
        ],
      },
      {
        title: t('editor.latex.zinc_dilute_hydrochloric_acid'),
        symbols: [
          {
            cmd: '\\ce{Zn + 2HCl = ZnC{l_2} + {H_2} \\uparrow}',
          },
        ],
      },
    ],
  },
});

// Keep the old export for backward compatibility
export const equationFormula = {
  symbols: {
    [EquationSymbolsEnum.math]: [
      {
        symbols: [
          {
            cmd: '\\times',
          },
          {
            cmd: '\\div',
          },
          {
            cmd: '\\pm',
          },
          {
            cmd: '\\mp',
          },
          {
            cmd: '\\cdot',
          },
          {
            cmd: '\\star',
          },
          {
            cmd: '\\%',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\sqrt{ab}',
          },
          {
            cmd: '\\sqrt[n]{ab}',
          },
          {
            cmd: '\\log_{a}{b}',
          },
          {
            cmd: '\\lg{ab}',
          },
          {
            cmd: 'a^{b}',
          },
          {
            cmd: 'a_{b}',
          },
          {
            cmd: 'c_a^b',
          },
          {
            cmd: '\\widetilde{ab}',
          },
          {
            cmd: '\\widehat{ab}',
          },
          {
            cmd: '\\overleftarrow{ab}',
          },
          {
            cmd: '\\overrightarrow{ab}',
          },
          {
            cmd: '\\overbrace{ab}',
          },
          {
            cmd: '\\underbrace{ab}',
          },
          {
            cmd: '\\underline{ab}',
          },
          {
            cmd: '\\overline{ab}',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\neq',
          },
          {
            cmd: '\\leq',
          },
          {
            cmd: '\\geq',
          },
          {
            cmd: '\\sim',
          },
          {
            cmd: '\\approx',
          },
          {
            cmd: '\\cong',
          },
          {
            cmd: '\\equiv',
          },
          {
            cmd: '\\propto',
          },
          {
            cmd: '\\ll',
          },
          {
            cmd: '\\gg',
          },
          {
            cmd: '\\in',
          },
          {
            cmd: '\\subset',
          },
          {
            cmd: '\\subseteq',
          },
          {
            cmd: '\\prec',
          },
          {
            cmd: '\\preceq',
          },
          {
            cmd: '\\simeq',
          },
          {
            cmd: '\\asymp',
          },
          {
            cmd: '\\doteq',
          },
          {
            cmd: '\\succ',
          },
          {
            cmd: '\\succeq',
          },
          {
            cmd: '\\sqsubseteq',
          },
          {
            cmd: '\\sqsupseteq',
          },
          {
            cmd: '\\ni',
          },
          {
            cmd: '\\models',
          },
          {
            cmd: '\\vdash',
          },
          {
            cmd: '\\dashv',
          },
          {
            cmd: '\\perp',
          },
          {
            cmd: '\\mid',
          },
          {
            cmd: '\\parallel',
          },
          {
            cmd: '\\smile',
          },
          {
            cmd: '\\frown',
          },
          {
            cmd: '\\bowtie',
          },
          {
            cmd: '\\unlhd',
          },
          {
            cmd: '\\unrhd',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\hat{a}',
          },
          {
            cmd: '\\check{a}',
          },
          {
            cmd: '\\breve{a}',
          },
          {
            cmd: '\\acute{a}',
          },
          {
            cmd: '\\grave{a}',
          },
          {
            cmd: '\\tilde{a}',
          },
          {
            cmd: '\\bar{a}',
          },
          {
            cmd: '\\vec{a}',
          },
          {
            cmd: '\\dot{a}',
          },
          {
            cmd: '\\ddot{a}',
          },
        ],
      },
    ],
    [EquationSymbolsEnum.func]: [
      {
        symbols: [
          {
            cmd: '\\frac{ab}{cd}',
          },
          {
            cmd: '\\frac{\\partial a}{\\partial b}',
          },
          {
            cmd: '\\frac{\\text{d}x}{\\text{d}y}',
          },
          {
            cmd: '\\lim\\limits_{a \\rightarrow b}',
          },
          {
            cmd: '\\int_{a}^{b}',
          },
          {
            cmd: '\\oint_a^b',
          },
          {
            cmd: '\\prod\\limits_a^b',
          },
          {
            cmd: '\\coprod\\limits_a^b',
          },
          {
            cmd: '\\bigcap\\limits_a^b',
          },
          {
            cmd: '\\bigcup\\limits_a^b',
          },
          {
            cmd: '\\bigvee\\limits_a^b',
          },
          {
            cmd: '\\bigwedge\\limits_a^b',
          },
          {
            cmd: '\\bigsqcup\\limits_a^b',
          },
          {
            cmd: '\\sum\\limits_a^b',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\left(\\begin{array}{c}a\\\\ b\\end{array}\\right)',
          },
          {
            cmd: '\\begin{bmatrix}a & b \\\\c & d \\end{bmatrix}',
          },
          {
            cmd: '\\begin{cases}a & x = 0\\\\b & x > 0\\end{cases}',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\arccos',
          },
          {
            cmd: '\\arcsin',
          },
          {
            cmd: '\\arctan',
          },
          {
            cmd: '\\cos',
          },
          {
            cmd: '\\cosh',
          },
          {
            cmd: '\\cot',
          },
          {
            cmd: '\\coth',
          },
          {
            cmd: '\\csc',
          },
          {
            cmd: '\\sec',
          },
          {
            cmd: '\\sin',
          },
          {
            cmd: '\\sinh',
          },
          {
            cmd: '\\tan',
          },
          {
            cmd: '\\tanh',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\exp',
          },
          {
            cmd: '\\log',
          },
          {
            cmd: '\\ln',
          },
          {
            cmd: '\\max',
          },
          {
            cmd: '\\min',
          },
          {
            cmd: '\\sup',
          },
          {
            cmd: '\\inf',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\lim',
          },
          {
            cmd: '\\gcd',
          },
          {
            cmd: '\\hom',
          },
          {
            cmd: '\\ker',
          },
          {
            cmd: '\\det',
          },
          {
            cmd: '\\bmod',
          },
        ],
      },
    ],
    [EquationSymbolsEnum.arrow]: [
      {
        symbols: [
          {
            cmd: '()',
          },
          {
            cmd: '[]',
          },
          {
            cmd: '\\{\\}',
          },
          {
            cmd: '\\lfloor',
          },
          {
            cmd: '\\lceil',
          },
          {
            cmd: '\\rfloor',
          },
          {
            cmd: '\\rceil',
          },
          {
            cmd: '\\langle',
          },
          {
            cmd: '\\rangle',
          },
          {
            cmd: '\\backslash',
          },
          {
            cmd: '\\cap',
          },
          {
            cmd: '\\cup',
          },
          {
            cmd: '\\uplus',
          },
          {
            cmd: '\\sqcap',
          },
          {
            cmd: '\\sqcup',
          },
          {
            cmd: '\\vee',
          },
          {
            cmd: '\\wedge',
          },
          {
            cmd: '\\wr',
          },
        ],
      },
      {
        symbols: [
          {
            cmd: '\\leftarrow',
          },
          {
            cmd: '\\Leftarrow',
          },
          {
            cmd: '\\rightarrow',
          },
          {
            cmd: '\\Rightarrow',
          },
          {
            cmd: '\\leftrightarrow',
          },
          {
            cmd: '\\Leftrightarrow',
          },
          {
            cmd: '\\mapsto',
          },
          {
            cmd: '\\leftharpoonup',
          },
          {
            cmd: '\\leftharpoondown',
          },
          {
            cmd: '\\uparrow',
          },
          {
            cmd: '\\downarrow',
          },
          {
            cmd: '\\leftleftarrows',
          },
          {
            cmd: '\\rightrightarrows',
          },
          {
            cmd: '\\leftrightarrows',
          },
          {
            cmd: '\\rightleftarrows',
          },
        ],
      },
    ],
    [EquationSymbolsEnum.greek]: [
      {
        symbols: [
          {
            cmd: '\\alpha',
          },
          {
            cmd: '\\beta',
          },
          {
            cmd: '\\gamma',
          },
          {
            cmd: '\\delta',
          },
          {
            cmd: '\\epsilon',
          },
          {
            cmd: '\\zeta',
          },
          {
            cmd: '\\eta',
          },
          {
            cmd: '\\theta',
          },
          {
            cmd: '\\iota',
          },
          {
            cmd: '\\kappa',
          },
          {
            cmd: '\\lambda',
          },
          {
            cmd: '\\mu',
          },
          {
            cmd: '\\nu',
          },
          {
            cmd: '\\xi',
          },
          {
            cmd: '\\pi',
          },
          {
            cmd: '\\rho',
          },
          {
            cmd: '\\sigma',
          },
          {
            cmd: '\\tau',
          },
          {
            cmd: '\\upsilon',
          },
          {
            cmd: '\\phi',
          },
          {
            cmd: '\\chi',
          },
          {
            cmd: '\\psi',
          },
          {
            cmd: '\\omega',
          },
        ],
      },
    ],
  },
  disciplineFormula: {
    [EquationTemplateMenuEnum.math]: [
      {
        titleKey: 'editor.latex.fourier_series',
        symbols: [
          {
            cmd: 'f(x) = {{{a_0}} \\over 2} + \\sum\\limits_{n = 1}^\\infty {({a_n}\\cos {nx} + {b_n}\\sin {nx})}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.taylor_expansion',
        symbols: [
          {
            cmd: 'e ^ { x } = 1 + \\frac { x } { 1 ! } + \\frac { x ^ { 2 } } { 2 ! } + \\frac { x ^ { 3 } } { 3 ! } + \\cdots , \\quad - \\infty < x < \\infty',
          },
        ],
      },
      {
        titleKey: 'editor.latex.gauss_formula',
        symbols: [
          {
            cmd: '\\iiint _ { \\Omega } \\left( \\frac { \\partial {P} } { \\partial {x} } + \\frac { \\partial {Q} } { \\partial {y} } + \\frac { \\partial {R} }{ \\partial {z} } \\right) \\mathrm { d } V = \\oint _ { \\partial \\Omega } ( P \\cos \\alpha + Q \\cos \\beta + R \\cos \\gamma ) \\mathrm{ d} S',
          },
        ],
      },
      {
        titleKey: 'editor.latex.definite_integral',
        symbols: [
          {
            cmd: '\\lim\\limits_ { n \\rightarrow + \\infty } \\sum _ { i = 1 } ^ { n } f \\left[ a + \\frac { i } { n } ( b - a ) \\right] \\frac { b - a } { n } = \\int _ { a } ^ { b } f ( x ) dx',
          },
        ],
      },
      {
        titleKey: 'editor.latex.expansion_of_sum',
        symbols: [
          {
            cmd: '( 1 + x ) ^ { n } = 1 + \\frac { n x } { 1 ! } + \\frac { n ( n - 1 ) x ^ { 2 } } { 2 ! } + \\cdots',
          },
        ],
      },
      {
        titleKey: 'editor.latex.trigonometric_identity_1',
        symbols: [
          {
            cmd: '\\sin \\alpha \\pm \\sin \\beta = 2 \\sin \\frac { 1 } { 2 } ( \\alpha \\pm \\beta ) \\cos \\frac { 1 } { 2 } ( \\alpha \\mp \\beta )',
          },
        ],
      },
      {
        titleKey: 'editor.latex.trigonometric_identity_2',
        symbols: [
          {
            cmd: '\\cos \\alpha + \\cos \\beta = 2 \\cos \\frac { 1 } { 2 } ( \\alpha + \\beta ) \\cos \\frac { 1 } { 2 } ( \\alpha - \\beta )',
          },
        ],
      },
      {
        titleKey: 'editor.latex.euler_formula',
        symbols: [
          {
            cmd: '{e^{ix}} = \\cos {x} + i\\sin {x}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.green_formula',
        symbols: [
          {
            cmd: '\\iint\\limits_D {({{\\partial Q} \\over {\\partial x}} - {{\\partial P} \\over {\\partial y}})dxdy = \\oint\\limits_L {Pdx + Qdy} }',
          },
        ],
      },
      {
        titleKey: 'editor.latex.bernoulli_equation',
        symbols: [
          {
            cmd: '{{dy} \\over {dx}} + P(x)y = Q(x){y^n}(n \\ne 0,1)',
          },
        ],
      },
      {
        titleKey: 'editor.latex.total_differential_equation',
        symbols: [
          {
            cmd: 'du(x,y) = P(x,y)dx + Q(x,y)dy = 0',
          },
        ],
      },
      {
        titleKey: 'editor.latex.inhomogeneous_equation',
        symbols: [
          {
            cmd: 'y = (\\int {Q(x){e^{\\int {P(x)dx} }}dx + C){e^{ - \\int {P(x)dx} }}}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.cauchy_mean_value_theorem',
        symbols: [
          {
            cmd: "\\frac{{f(b) - f(a)}}{{F(b) - F(a)}} = \\frac{{f'(\\xi )}}{{F'(\\xi )}}",
          },
        ],
      },
      {
        titleKey: 'editor.latex.lagrange_mean_value_theorem',
        symbols: [
          {
            cmd: "f(b) - f(a) = f'(\\xi )(b - a)",
          },
        ],
      },
      {
        titleKey: 'editor.latex.surface_area',
        symbols: [
          {
            cmd: 'A = \\iint\\limits_D {\\sqrt {1 + {{\\left( {{{\\partial z} \\over {\\partial x}}} \\right)}^2} + {{\\left( {{{\\partial z} \\over {\\partial y}}} \\right)}^2}} dxdy}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.multiple_integral',
        symbols: [
          {
            cmd: "\\iint\\limits_D {f(x,y)dxdy} = \\iint\\limits_{D'} {f(r\\cos \\theta ,r\\sin \\theta )rdrd\\theta}",
          },
        ],
      },
      {
        titleKey: 'editor.latex.derivative_formula',
        symbols: [
          {
            cmd: "(\\arcsin x)' = \\frac{1}{{\\sqrt {1 - {x^2}} }}",
          },
        ],
      },
      {
        titleKey: 'editor.latex.trigonometric_function_integral',
        symbols: [
          {
            cmd: '\\int {tgxdx = - \\ln \\left| {\\cos x} \\right| + C}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.quadric_surface',
        symbols: [
          {
            cmd: '\\frac{{{x^2}}}{{{a^2}}} + \\frac{{{y^2}}}{{{b^2}}} - \\frac{{{z^2}}}{{{c^2}}} = 1',
          },
        ],
      },
      {
        titleKey: 'editor.latex.second_order_derivative',
        symbols: [
          {
            cmd: '{{{d^2}y} \\over {d{x^2}}} + P(x){{dy} \\over {dx}} + Q(x)y = f(x)',
          },
        ],
      },
      {
        titleKey: 'editor.latex.directional_derivative',
        symbols: [
          {
            cmd: '\\frac{{\\partial f}}{{\\partial l}} = \\frac{{\\partial f}}{{\\partial x}}\\cos \\phi + \\frac{{\\partial f}}{{\\partial y}}\\sin \\phi',
          },
        ],
      },
    ],
    [EquationTemplateMenuEnum.physical]: [
      {
        titleKey: 'editor.latex.newton_first_law',
        symbols: [
          {
            cmd: '\\sum \\vec{F}_{i}=\\frac{d \\vec{v}}{d t}=0',
          },
        ],
      },
      {
        titleKey: 'editor.latex.newton_second_law',
        symbols: [
          {
            cmd: '\\vec{F}=m \\vec{a}=m \\frac{d^{2} \\vec{r}}{d t^{2}}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.newton_third_law',
        symbols: [
          {
            cmd: '\\vec{F}_{12}=-\\vec{F}_{21}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.gravitational_potential_energy',
        symbols: [
          {
            cmd: '{E_p} = -\\frac{{GMm}}{r}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.coulomb_law',
        symbols: [
          {
            cmd: '\\vec{F}=k \\frac{Q q}{r^{2}} \\hat{r}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.electrostatic_field_loop_theorem',
        symbols: [
          {
            cmd: '\\oint_{L} \\vec{E} \\cdot d \\vec{I}=0',
          },
        ],
      },
      {
        titleKey: 'editor.latex.biot_savart_law',
        symbols: [
          {
            cmd: 'd \\vec{B}=\\frac{\\mu_{0}}{4 \\pi} \\frac{I d I \\times \\vec{r}}{r^{3}}=\\frac{\\mu_{0}}{4 \\pi} \\frac{I d I \\sin \\theta}{r^{2}}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.ampere_law',
        symbols: [
          {
            cmd: 'd \\vec{F}= Id \\vec{l} \\times \\vec{B}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.faraday_electromagnetic_induction_law',
        symbols: [
          {
            cmd: 'E = n{{ \\Delta \\Phi } \\over {\\Delta {t} }}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.gauss_law',
        symbols: [
          {
            cmd: '\\left.\\Phi_{e}=\\oint \\vec{E} \\cdot d \\vec{S}=\\frac{1}{\\varepsilon_{0}}\\right\\rangle q',
          },
        ],
      },
      {
        titleKey: 'editor.latex.electric_field_loop_theorem',
        symbols: [
          {
            cmd: '\\oint \\vec{E} \\cdot d \\vec{I}=-\\frac{d \\varphi_{B}}{d t}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.ampere_loop_theorem',
        symbols: [
          {
            cmd: '\\oint \\vec{B} \\cdot d \\vec{I}=\\mu_{0} I+\\mu_{0} I_{d}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.joule_law',
        symbols: [
          {
            cmd: 'Q = I ^ { 2 } R \\mathrm { t }',
          },
        ],
      },
      {
        titleKey: 'editor.latex.law_of_universal_gravitation',
        symbols: [
          {
            cmd: 'F = G{{Mm} \\over {{r^2}}}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.einstein_photoelectric_equation',
        symbols: [
          {
            cmd: '{E_k} = hv - {W_0}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.de_broglie_wavelength',
        symbols: [
          {
            cmd: '\\lambda = \\frac{{ \\frac{{{c^2}}}{v}}}{{ \\frac{{m{c^2}}}{h}}} = \\frac{h}{{mv}} = \\frac{h}{p}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.heisenberg_uncertainty_principle',
        symbols: [
          {
            cmd: '\\Delta {x} \\Delta {p} \\ge \\frac{h}{{4 \\pi }}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.special_relativity_length',
        symbols: [
          {
            cmd: 'l = {l_0} \\sqrt {1 - {{( \\frac{v}{c})}^2}}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.simple_harmonic_motion',
        symbols: [
          {
            cmd: '{y_0} = A \\cos ( \\omega {t} + { \\varphi _0})',
          },
        ],
      },
      {
        titleKey: 'editor.latex.wave_equation',
        symbols: [
          {
            cmd: 'y(t) = A \\cos ( \\frac{{2 \\pi {x}}}{ \\lambda } + \\varphi )',
          },
        ],
      },
      {
        titleKey: 'editor.latex.doppler_effect',
        symbols: [
          {
            cmd: "f' = \\left( { \\frac{{v \\pm {v_0}}}{{v \\pm {v_s}}}} \\right)f",
          },
        ],
      },
    ],
    [EquationTemplateMenuEnum.chemistry]: [
      {
        titleKey: 'editor.latex.aluminum_dilute_sulfuric_acid',
        symbols: [
          {
            cmd: '\\ce{2Al + 3{H_2}S{O_4} = A{l_2}{(S{O_4})_3} + 3{H_2} \\uparrow}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.aluminum_dilute_hydrochloric_acid',
        symbols: [
          {
            cmd: '\\ce{2Al + 6HCl = 2AlC{l_3} + 3{H_2} \\uparrow}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.magnesium_dilute_sulfuric_acid',
        symbols: [
          {
            cmd: '\\ce{Mg + {H_2}S{O_4} = MgS{O_4} + {H_2} \\uparrow}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.magnesium_dilute_hydrochloric_acid',
        symbols: [
          {
            cmd: '\\ce{Mg + 2HCl = MgC{l_2} + {H_2} \\uparrow}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.iron_copper_sulfate_solution',
        symbols: [
          {
            cmd: '\\ce{Fe + CuS{O_4} = FeS{O_4} + Cu}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.iron_dilute_sulfuric_acid',
        symbols: [
          {
            cmd: '\\ce{Fe + {H_2}S{O_4} = FeS{O_4} + {H_2} \\uparrow}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.iron_dilute_hydrochloric_acid',
        symbols: [
          {
            cmd: '\\ce{Fe + 2HCl = FeC{l_2} + {H_2} \\uparrow}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.zinc_dilute_sulfuric_acid',
        symbols: [
          {
            cmd: '\\ce{Zn + {H_2}S{O_4} = ZnS{O_4} + {H_2} \\uparrow}',
          },
        ],
      },
      {
        titleKey: 'editor.latex.zinc_dilute_hydrochloric_acid',
        symbols: [
          {
            cmd: '\\ce{Zn + 2HCl = ZnC{l_2} + {H_2} \\uparrow}',
          },
        ],
      },
    ],
  },
};
